import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { kaireraAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Calculator, MessageSquare, Loader2, ArrowRight, RotateCcw,
  Send, TrendingUp, DollarSign, Shield, Zap, Target,
  ChevronRight, AlertTriangle, CheckCircle, Scale, Mic, MicOff,
  Volume2, VolumeX, User, Briefcase, BarChart3, Lightbulb,
  Award, X, Play, Square
} from 'lucide-react';

const TABS = [
  { id: 'calculator', label: 'Decision Calculator', icon: Calculator, desc: 'Compare career paths' },
  { id: 'negotiation', label: 'Salary Negotiation', icon: MessageSquare, desc: 'Practice with AI' },
];

const DIFFICULTIES = [
  { id: 'easy', label: 'Easy', color: '#16A34A', desc: 'Friendly, quick to compromise', icon: Shield },
  { id: 'medium', label: 'Medium', color: '#F5841F', desc: 'Professional, moderate pushback', icon: Target },
  { id: 'tough', label: 'Tough', color: '#DC2626', desc: 'Aggressive hardball tactics', icon: AlertTriangle },
];

const VOICES = [
  { id: 'echo', label: 'Male', desc: 'Deep, authoritative', icon: '♂' },
  { id: 'nova', label: 'Female', desc: 'Warm, professional', icon: '♀' },
];

// ---- Extracted components (outside parent to avoid remount on state change) ----
const GROWTH_OPTIONS = ['low', 'moderate', 'high', 'very high'];
const RISK_OPTIONS = ['low', 'medium', 'medium-high', 'high'];
const TIMELINE_OPTIONS = ['1 month', '3 months', '6 months', '1 year', '2+ years'];

function ChipSelect({ options, value, onChange, color }) {
  return (
    <div className="flex gap-1.5 flex-wrap">
      {options.map(o => (
        <button key={o} onClick={() => onChange(o)}
          className="px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all"
          style={{
            background: value === o ? `${color}15` : 'var(--t-bg)',
            border: `1px solid ${value === o ? color : 'var(--t-border)'}`,
            color: value === o ? color : 'var(--t-text-muted)',
          }}>{o}</button>
      ))}
    </div>
  );
}

function PathForm({ label, color, path, setPath, testId, goals, onSelectGoal }) {
  return (
    <div className="nx-card p-5 flex-1" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black text-white"
          style={{ background: color }}>{label}</div>
        <div>
          <span className="text-xs font-bold" style={{ color }}>Path {label}</span>
          {goals.length > 0 && (
            <div className="flex gap-1 mt-0.5">
              {goals.map((g, i) => (
                <button key={g.id} onClick={() => onSelectGoal(label.toLowerCase(), g)}
                  className="text-[8px] font-semibold px-1.5 py-0.5 rounded transition-all"
                  style={{
                    background: path.title === g.name ? `${color}10` : 'transparent',
                    color: path.title === g.name ? color : 'var(--t-text-muted)',
                    border: `1px solid ${path.title === g.name ? `${color}30` : 'transparent'}`,
                  }}
                  data-testid={`goal-chip-${label.toLowerCase()}-${i}`}>
                  {g.name.length > 20 ? g.name.slice(0, 20) + '...' : g.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="space-y-3">
        <div>
          <Label className="text-[10px] font-semibold" style={{ color: 'var(--t-text-muted)' }}>Role / Title</Label>
          <Input value={path.title} onChange={e => setPath({ ...path, title: e.target.value })}
            placeholder="e.g., Senior Product Manager"
            className="h-9 text-sm mt-1" style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
        </div>
        <div>
          <Label className="text-[10px] font-semibold" style={{ color: 'var(--t-text-muted)' }}>Annual Salary ($)</Label>
          <Input value={path.salary} onChange={e => setPath({ ...path, salary: e.target.value })}
            placeholder="120000" type="number"
            className="h-9 text-sm mt-1" style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
        </div>
        <div>
          <Label className="text-[10px] font-semibold mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Growth Potential</Label>
          <ChipSelect options={GROWTH_OPTIONS} value={path.growth} onChange={v => setPath({ ...path, growth: v })} color={color} />
        </div>
        <div>
          <Label className="text-[10px] font-semibold mb-1 block" style={{ color: 'var(--t-text-muted)' }}>
            Satisfaction: <span style={{ color }}>{path.satisfaction}/10</span>
          </Label>
          <input type="range" min="1" max="10" value={path.satisfaction}
            onChange={e => setPath({ ...path, satisfaction: parseInt(e.target.value) })}
            className="w-full accent-[#0D9488]" />
        </div>
        <div>
          <Label className="text-[10px] font-semibold mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Risk Level</Label>
          <ChipSelect options={RISK_OPTIONS} value={path.risk} onChange={v => setPath({ ...path, risk: v })} color={color} />
        </div>
        <div>
          <Label className="text-[10px] font-semibold mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Timeline</Label>
          <ChipSelect options={TIMELINE_OPTIONS} value={path.timeline} onChange={v => setPath({ ...path, timeline: v })} color={color} />
        </div>
      </div>
    </div>
  );
}

// ---- Career Decision Calculator ----
function CareerCalculator() {
  const [pathA, setPathA] = useState({ title: '', salary: '', growth: 'moderate', satisfaction: 7, risk: 'medium', timeline: '6 months' });
  const [pathB, setPathB] = useState({ title: '', salary: '', growth: 'moderate', satisfaction: 7, risk: 'medium', timeline: '6 months' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [goals, setGoals] = useState([]);
  const [goalsLoading, setGoalsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await kaireraAPI.getGoalsForCalculator();
        const g = res.data.goals || [];
        const validGoals = g.filter(x => x.name);
        setGoals(validGoals);
        if (validGoals.length >= 1) setPathA(prev => ({ ...prev, title: validGoals[0].name }));
        if (validGoals.length >= 2) setPathB(prev => ({ ...prev, title: validGoals[1].name }));
      } catch {} finally { setGoalsLoading(false); }
    })();
  }, []);

  const handleCompare = async () => {
    if (!pathA.title || !pathB.title) return;
    setLoading(true);
    try {
      const res = await kaireraAPI.careerCalculator({
        path_a_title: pathA.title, path_a_salary: parseFloat(pathA.salary) || 0,
        path_a_growth: pathA.growth, path_a_satisfaction: pathA.satisfaction,
        path_a_risk: pathA.risk, path_a_timeline: pathA.timeline,
        path_b_title: pathB.title, path_b_salary: parseFloat(pathB.salary) || 0,
        path_b_growth: pathB.growth, path_b_satisfaction: pathB.satisfaction,
        path_b_risk: pathB.risk, path_b_timeline: pathB.timeline,
      });
      setResult(res.data);
    } catch {} finally { setLoading(false); }
  };

  const selectGoal = useCallback((slot, goal) => {
    if (slot === 'a') setPathA(prev => ({ ...prev, title: goal.name }));
    else setPathB(prev => ({ ...prev, title: goal.name }));
  }, []);

  return (
    <div className="space-y-5" data-testid="career-calculator">
      {goals.filter(g => g.name).length > 0 && !goalsLoading && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(13,148,136,0.06)', border: '1px solid rgba(13,148,136,0.12)' }}>
          <Briefcase className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#0D9488' }} />
          <span className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>
            Pre-loaded from your career goals. Edit freely or compare different paths.
          </span>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-4">
        <PathForm label="A" color="#0D9488" path={pathA} setPath={setPathA} testId="path-a-form" goals={goals} onSelectGoal={selectGoal} />
        <div className="hidden lg:flex items-center">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
            <Scale className="w-4 h-4" style={{ color: 'var(--t-text-muted)' }} />
          </div>
        </div>
        <PathForm label="B" color="#F5841F" path={pathB} setPath={setPathB} testId="path-b-form" goals={goals} onSelectGoal={selectGoal} />
      </div>

      <Button onClick={handleCompare} disabled={!pathA.title || !pathB.title || loading}
        className="w-full h-11 font-bold rounded-xl text-sm"
        style={{ background: 'linear-gradient(135deg, #0D9488, #0F766E)', color: '#fff', opacity: (pathA.title && pathB.title) ? 1 : 0.5 }}
        data-testid="compare-btn">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Scale className="w-4 h-4 mr-2" />}
        Analyze & Compare
      </Button>

      {result && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <div className="nx-card p-6" data-testid="calc-result">
            <div className="flex items-center justify-center gap-8 mb-5">
              {[
                { label: 'A', color: '#0D9488', score: result.path_a.score, title: result.path_a.title, isWinner: result.winner === 'a' },
                null,
                { label: 'B', color: '#F5841F', score: result.path_b.score, title: result.path_b.title, isWinner: result.winner === 'b' },
              ].map((item, idx) => item === null ? (
                <div key="vs" className="text-xs font-black px-3 py-1 rounded-full" style={{ background: 'var(--t-bg-section)', color: 'var(--t-text-muted)' }}>VS</div>
              ) : (
                <div key={item.label} className="text-center relative">
                  {item.isWinner && (
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2">
                      <Award className="w-5 h-5" style={{ color: item.color }} />
                    </div>
                  )}
                  <div className="text-4xl font-black" style={{ color: item.isWinner ? item.color : 'var(--t-text-muted)' }}>
                    {item.score}
                  </div>
                  <p className="text-[10px] font-bold mt-1" style={{ color: item.color }}>Path {item.label}</p>
                  <p className="text-[9px] truncate max-w-[140px]" style={{ color: 'var(--t-text-muted)' }}>{item.title}</p>
                </div>
              ))}
            </div>

            {['salary', 'growth', 'satisfaction', 'risk'].map(metric => {
              const a = result.path_a.breakdown[metric];
              const b = result.path_b.breakdown[metric];
              return (
                <div key={metric} className="flex items-center gap-3 mb-2.5">
                  <span className="text-[10px] font-bold w-24 capitalize" style={{ color: 'var(--t-text-muted)' }}>{metric}</span>
                  <div className="flex-1 flex items-center gap-1">
                    <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: 'var(--t-border)' }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${(a / 10) * 100}%`, background: '#0D9488' }} />
                    </div>
                    <span className="text-[9px] font-black w-6 text-center" style={{ color: '#0D9488' }}>{a}</span>
                  </div>
                  <div className="flex-1 flex items-center gap-1">
                    <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: 'var(--t-border)' }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${(b / 10) * 100}%`, background: '#F5841F' }} />
                    </div>
                    <span className="text-[9px] font-black w-6 text-center" style={{ color: '#F5841F' }}>{b}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="nx-card p-5" style={{ borderLeft: '4px solid #0D9488' }} data-testid="calc-recommendation">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="w-4 h-4" style={{ color: '#0D9488' }} />
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#0D9488' }}>AI Recommendation</span>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>{result.recommendation}</p>
          </div>
        </motion.div>
      )}
    </div>
  );
}


// ---- Salary Negotiation Simulator ----
function NegotiationSimulator() {
  const [setup, setSetup] = useState({ role: '', current: '', target: '', difficulty: 'medium' });
  const [started, setStarted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState('echo');
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleStart = async () => {
    if (!setup.role) return;
    setLoading(true);
    setStarted(true);
    try {
      const res = await kaireraAPI.negotiate({
        role_title: setup.role,
        current_salary: parseFloat(setup.current) || null,
        target_salary: parseFloat(setup.target) || null,
        difficulty: setup.difficulty,
      });
      const msgs = res.data.messages || [];
      setMessages(msgs);
      if (voiceMode && msgs.length > 0) {
        const lastManager = msgs.filter(m => m.role === 'manager').pop();
        if (lastManager) playTTS(lastManager.content);
      }
    } catch {} finally { setLoading(false); }
  };

  const handleSend = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const res = await kaireraAPI.negotiate({ message: msg, difficulty: setup.difficulty });
      const msgs = res.data.messages || [];
      setMessages(msgs);
      if (voiceMode) {
        const lastManager = msgs.filter(m => m.role === 'manager').pop();
        if (lastManager) playTTS(lastManager.content);
      }
    } catch {} finally { setLoading(false); }
  };

  const handleReset = async () => {
    await kaireraAPI.resetNegotiation();
    setMessages([]);
    setStarted(false);
    setShowInsights(false);
    setInsights(null);
    setSetup({ role: '', current: '', target: '', difficulty: 'medium' });
  };

  const playTTS = async (text) => {
    try {
      setIsPlaying(true);
      const res = await kaireraAPI.textToSpeech(text, selectedVoice);
      const audioB64 = res.data.audio;
      const audio = new Audio(`data:audio/mp3;base64,${audioB64}`);
      audioRef.current = audio;
      audio.onended = () => setIsPlaying(false);
      audio.onerror = () => setIsPlaying(false);
      await audio.play();
    } catch { setIsPlaying(false); }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        try {
          setLoading(true);
          const res = await kaireraAPI.speechToText(blob);
          const text = res.data.text;
          if (text) handleSend(text);
          else setLoading(false);
        } catch { setLoading(false); }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch {
      alert('Microphone access denied. Please enable microphone permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const loadInsights = async () => {
    setShowInsights(true);
    setInsightsLoading(true);
    try {
      const res = await kaireraAPI.getNegotiationInsights();
      setInsights(res.data.insights);
    } catch {} finally { setInsightsLoading(false); }
  };

  // Setup screen
  if (!started) {
    const diff = DIFFICULTIES.find(d => d.id === setup.difficulty);
    return (
      <div className="space-y-5" data-testid="negotiation-setup">
        <div className="nx-card p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(13,148,136,0.08)' }}>
              <DollarSign className="w-5 h-5" style={{ color: '#0D9488' }} />
            </div>
            <div>
              <h3 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Configure Your Negotiation</h3>
              <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>Practice with a virtual hiring manager. Set difficulty and voice preferences.</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--t-text-muted)' }}>Role</Label>
              <Input value={setup.role} onChange={e => setSetup({ ...setup, role: e.target.value })}
                placeholder="e.g., Senior Software Engineer at Google"
                className="h-10 text-sm mt-1.5" style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }}
                data-testid="neg-role-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--t-text-muted)' }}>Current Salary ($)</Label>
                <Input value={setup.current} onChange={e => setSetup({ ...setup, current: e.target.value })}
                  placeholder="120000" type="number"
                  className="h-10 text-sm mt-1.5" style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }}
                  data-testid="neg-current-input" />
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--t-text-muted)' }}>Target Salary ($)</Label>
                <Input value={setup.target} onChange={e => setSetup({ ...setup, target: e.target.value })}
                  placeholder="160000" type="number"
                  className="h-10 text-sm mt-1.5" style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }}
                  data-testid="neg-target-input" />
              </div>
            </div>

            {/* Difficulty Selector */}
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider mb-2 block" style={{ color: 'var(--t-text-muted)' }}>
                Difficulty Level
              </Label>
              <div className="grid grid-cols-3 gap-2" data-testid="difficulty-selector">
                {DIFFICULTIES.map(d => {
                  const DIcon = d.icon;
                  const isActive = setup.difficulty === d.id;
                  return (
                    <button key={d.id} onClick={() => setSetup({ ...setup, difficulty: d.id })}
                      className="p-3 rounded-xl text-center transition-all"
                      style={{
                        background: isActive ? `${d.color}10` : 'var(--t-bg)',
                        border: `2px solid ${isActive ? d.color : 'var(--t-border)'}`,
                      }}
                      data-testid={`difficulty-${d.id}`}>
                      <DIcon className="w-5 h-5 mx-auto mb-1" style={{ color: isActive ? d.color : 'var(--t-text-muted)' }} />
                      <p className="text-xs font-bold" style={{ color: isActive ? d.color : 'var(--t-text-heading)' }}>{d.label}</p>
                      <p className="text-[9px] mt-0.5" style={{ color: 'var(--t-text-muted)' }}>{d.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Voice Mode */}
            <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'var(--t-bg)', border: '1px solid var(--t-border)' }}>
              <div className="flex items-center gap-2">
                <Volume2 className="w-4 h-4" style={{ color: voiceMode ? '#8B5CF6' : 'var(--t-text-muted)' }} />
                <div>
                  <p className="text-xs font-bold" style={{ color: 'var(--t-text-heading)' }}>Voice Mode</p>
                  <p className="text-[9px]" style={{ color: 'var(--t-text-muted)' }}>Speak and listen to the hiring manager</p>
                </div>
              </div>
              <button onClick={() => setVoiceMode(!voiceMode)}
                className="relative w-11 h-6 rounded-full transition-all"
                style={{ background: voiceMode ? '#8B5CF6' : 'var(--t-border)' }}
                data-testid="voice-toggle">
                <div className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all"
                  style={{ left: voiceMode ? 22 : 2 }} />
              </button>
            </div>

            {/* Voice Selection */}
            <AnimatePresence>
              {voiceMode && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                  <Label className="text-[10px] font-bold uppercase tracking-wider mb-2 block" style={{ color: 'var(--t-text-muted)' }}>
                    Hiring Manager Voice
                  </Label>
                  <div className="grid grid-cols-2 gap-2" data-testid="voice-selector">
                    {VOICES.map(v => {
                      const isActive = selectedVoice === v.id;
                      return (
                        <button key={v.id} onClick={() => setSelectedVoice(v.id)}
                          className="p-3 rounded-xl flex items-center gap-3 transition-all"
                          style={{
                            background: isActive ? 'rgba(139,92,246,0.08)' : 'var(--t-bg)',
                            border: `2px solid ${isActive ? '#8B5CF6' : 'var(--t-border)'}`,
                          }}
                          data-testid={`voice-${v.id}`}>
                          <span className="text-lg">{v.icon}</span>
                          <div className="text-left">
                            <p className="text-xs font-bold" style={{ color: isActive ? '#8B5CF6' : 'var(--t-text-heading)' }}>{v.label}</p>
                            <p className="text-[9px]" style={{ color: 'var(--t-text-muted)' }}>{v.desc}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Button onClick={handleStart} disabled={!setup.role || loading}
            className="w-full h-11 mt-5 font-bold rounded-xl text-sm"
            style={{ background: 'linear-gradient(135deg, #0D9488, #0F766E)', color: '#fff', opacity: setup.role ? 1 : 0.5 }}
            data-testid="start-negotiation-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageSquare className="w-4 h-4 mr-2" />}
            Begin Negotiation
          </Button>
        </div>
      </div>
    );
  }

  const diffColor = DIFFICULTIES.find(d => d.id === setup.difficulty)?.color || '#F5841F';
  const exchangeCount = messages.filter(m => m.role === 'manager').length;

  // Insights view
  if (showInsights) {
    return (
      <div className="space-y-5" data-testid="negotiation-insights">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" style={{ color: '#8B5CF6' }} />
            <h3 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Performance Report</h3>
          </div>
          <Button onClick={() => setShowInsights(false)} className="h-8 px-3 text-xs rounded-lg"
            style={{ background: 'var(--t-bg-section)', color: 'var(--t-text)', border: '1px solid var(--t-border)' }}
            data-testid="back-to-chat-btn">
            Back to Chat
          </Button>
        </div>

        {insightsLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin mr-2" style={{ color: '#8B5CF6' }} />
            <span className="text-sm" style={{ color: 'var(--t-text-muted)' }}>Analyzing your negotiation performance...</span>
          </div>
        ) : insights ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            {/* Score Hero */}
            <div className="nx-card p-6 text-center" data-testid="insights-score">
              <div className="text-5xl font-black" style={{ color: insights.overall_score >= 7 ? '#16A34A' : insights.overall_score >= 5 ? '#F5841F' : '#DC2626' }}>
                {insights.grade}
              </div>
              <p className="text-lg font-bold mt-1" style={{ color: 'var(--t-text-heading)' }}>{insights.overall_score}/10</p>
              <p className="text-xs mt-2 max-w-md mx-auto" style={{ color: 'var(--t-text-muted)' }}>{insights.verdict}</p>
              <div className="flex items-center justify-center gap-1 mt-2">
                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full"
                  style={{ background: `${diffColor}10`, color: diffColor, border: `1px solid ${diffColor}20` }}>
                  {setup.difficulty.toUpperCase()} MODE
                </span>
              </div>
            </div>

            {/* What went well + wrong */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="nx-card p-4" data-testid="insights-well">
                <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#16A34A' }}>What Went Well</p>
                {(insights.what_went_well || []).map((item, i) => (
                  <div key={i} className="flex items-start gap-2 py-1.5">
                    <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: '#16A34A' }} />
                    <span className="text-xs" style={{ color: 'var(--t-text-secondary)' }}>{item}</span>
                  </div>
                ))}
              </div>
              <div className="nx-card p-4" data-testid="insights-wrong">
                <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#DC2626' }}>Where You Went Wrong</p>
                {(insights.where_you_went_wrong || []).map((item, i) => (
                  <div key={i} className="flex items-start gap-2 py-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: '#DC2626' }} />
                    <span className="text-xs" style={{ color: 'var(--t-text-secondary)' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tactics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="nx-card p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#0D9488' }}>Tactics You Used</p>
                {(insights.tactics_used || []).map((t, i) => (
                  <div key={i} className="flex items-center gap-2 py-1">
                    <Zap className="w-3 h-3 flex-shrink-0" style={{ color: '#0D9488' }} />
                    <span className="text-xs" style={{ color: 'var(--t-text-secondary)' }}>{t}</span>
                  </div>
                ))}
              </div>
              <div className="nx-card p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#F5841F' }}>Missed Opportunities</p>
                {(insights.missed_opportunities || []).concat(insights.tactics_missed || []).map((t, i) => (
                  <div key={i} className="flex items-center gap-2 py-1">
                    <Target className="w-3 h-3 flex-shrink-0" style={{ color: '#F5841F' }} />
                    <span className="text-xs" style={{ color: 'var(--t-text-secondary)' }}>{t}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tips */}
            <div className="nx-card p-4" style={{ borderLeft: '4px solid #8B5CF6' }} data-testid="insights-tips">
              <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#8B5CF6' }}>Improvement Tips</p>
              {(insights.improvement_tips || []).map((tip, i) => (
                <div key={i} className="flex items-start gap-2 py-1.5">
                  <Lightbulb className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: '#8B5CF6' }} />
                  <span className="text-xs" style={{ color: 'var(--t-text-secondary)' }}>{tip}</span>
                </div>
              ))}
            </div>

            <Button onClick={handleReset} className="w-full h-10 rounded-xl text-xs font-bold"
              style={{ background: 'var(--t-bg-section)', color: 'var(--t-text)', border: '1px solid var(--t-border)' }}
              data-testid="try-again-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Start New Negotiation
            </Button>
          </motion.div>
        ) : (
          <div className="text-center py-12">
            <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>
              Complete at least 2 exchanges to get performance insights.
            </p>
          </div>
        )}
      </div>
    );
  }

  // Chat view
  return (
    <div className="flex flex-col" style={{ minHeight: '55vh' }} data-testid="negotiation-chat">
      {/* Header Bar */}
      <div className="flex items-center justify-between mb-4 p-3 rounded-xl"
        style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${diffColor}12` }}>
            <DollarSign className="w-4 h-4" style={{ color: diffColor }} />
          </div>
          <div>
            <p className="text-xs font-bold" style={{ color: 'var(--t-text-heading)' }}>{setup.role}</p>
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                style={{ background: `${diffColor}10`, color: diffColor }}>{setup.difficulty.toUpperCase()}</span>
              <span className="text-[9px]" style={{ color: 'var(--t-text-muted)' }}>{exchangeCount} exchanges</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {exchangeCount >= 2 && (
            <Button onClick={loadInsights} className="h-8 px-3 text-[10px] font-bold rounded-lg gap-1"
              style={{ background: 'rgba(139,92,246,0.08)', color: '#8B5CF6', border: '1px solid rgba(139,92,246,0.2)' }}
              data-testid="view-insights-btn">
              <BarChart3 className="w-3 h-3" /> Insights
            </Button>
          )}
          <button onClick={handleReset} className="h-8 w-8 rounded-lg flex items-center justify-center"
            style={{ background: 'var(--t-bg)', border: '1px solid var(--t-border)' }}
            data-testid="neg-reset-btn">
            <RotateCcw className="w-3.5 h-3.5" style={{ color: 'var(--t-text-muted)' }} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 space-y-3 mb-4 overflow-y-auto pr-1" style={{ maxHeight: 420 }}>
        {messages.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] ${m.role === 'user' ? '' : ''}`}>
              {m.role !== 'user' && (
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: `${diffColor}15` }}>
                    <Briefcase className="w-2.5 h-2.5" style={{ color: diffColor }} />
                  </div>
                  <span className="text-[9px] font-bold" style={{ color: diffColor }}>Hiring Manager</span>
                  {voiceMode && (
                    <button onClick={() => playTTS(m.content)} className="p-0.5 rounded"
                      style={{ color: 'var(--t-text-muted)' }} data-testid={`play-msg-${i}`}>
                      <Volume2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}
              <div className="px-4 py-3 rounded-2xl text-sm leading-relaxed"
                style={{
                  background: m.role === 'user' ? '#0D9488' : 'var(--t-bg-section)',
                  color: m.role === 'user' ? '#fff' : 'var(--t-text)',
                  border: m.role === 'user' ? 'none' : '1px solid var(--t-border)',
                  borderTopRightRadius: m.role === 'user' ? 4 : undefined,
                  borderTopLeftRadius: m.role !== 'user' ? 4 : undefined,
                }}>
                {m.content}
              </div>
            </div>
          </motion.div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="px-4 py-3 rounded-2xl" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
              <div className="flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: diffColor }} />
                <span className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>Thinking...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="flex gap-2 items-center">
        {voiceMode && (
          <>
            {isPlaying ? (
              <button onClick={stopAudio}
                className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: '#DC2626', color: '#fff' }}
                data-testid="stop-audio-btn">
                <Square className="w-4 h-4" />
              </button>
            ) : null}
            <button
              onClick={isRecording ? stopRecording : startRecording}
              className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${isRecording ? 'animate-pulse' : ''}`}
              style={{
                background: isRecording ? '#DC2626' : 'rgba(139,92,246,0.1)',
                color: isRecording ? '#fff' : '#8B5CF6',
                border: isRecording ? 'none' : '1px solid rgba(139,92,246,0.2)',
              }}
              data-testid="mic-btn">
              {isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
          </>
        )}
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
          placeholder={voiceMode ? "Type or use mic..." : "Make your case..."}
          className="flex-1 h-10 px-4 rounded-xl text-sm"
          style={{ background: 'var(--t-bg-input)', border: '1px solid var(--t-border)', color: 'var(--t-text)' }}
          data-testid="neg-input" />
        <Button onClick={() => handleSend()} disabled={!input.trim() || loading}
          className="h-10 px-4 rounded-xl" style={{ background: '#0D9488', color: '#fff' }}
          data-testid="neg-send-btn">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}


// ---- MAIN PAGE ----
export default function KaireraToolsPage() {
  const [activeTab, setActiveTab] = useState('calculator');

  return (
    <div className="max-w-4xl mx-auto" data-testid="tools-page">
      {/* Header */}
      <div className="mb-6">
        <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: '#0D9488' }}>
          Career Intelligence Engine
        </p>
        <h1 className="text-2xl font-extrabold" style={{ color: 'var(--t-text-heading)' }}>
          Interactive Tools
        </h1>
        <p className="text-xs mt-1" style={{ color: 'var(--t-text-muted)' }}>
          Data-driven tools to make smarter career decisions.
        </p>
      </div>

      {/* Enterprise Tabs */}
      <div className="flex gap-1 border-b mb-6" style={{ borderColor: 'var(--t-border)' }}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-2 px-5 py-3 -mb-px transition-all"
              style={{
                borderBottom: isActive ? '2px solid #0D9488' : '2px solid transparent',
                color: isActive ? '#0D9488' : 'var(--t-text-muted)',
              }}
              data-testid={`tab-${tab.id}`}>
              <Icon className="w-4 h-4" />
              <div className="text-left">
                <p className="text-xs font-bold">{tab.label}</p>
                <p className="text-[9px]" style={{ color: 'var(--t-text-muted)' }}>{tab.desc}</p>
              </div>
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.15 }}>
          {activeTab === 'calculator' && <CareerCalculator />}
          {activeTab === 'negotiation' && <NegotiationSimulator />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
